import React from 'react';
import { StyleSheet, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import Animated, { SharedValue, useAnimatedStyle, useDerivedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation } from '@react-navigation/native';

import { CloseIcon } from '@/assets/icons/close';
import { HistoryIcon } from '@/assets/icons/history';
import { InfoOutlinedIcon } from '@/assets/icons/info-outlined';
import { SCREEN_NAV_BAR_HEIGHT, SHEET_NAV_BAR_TOP_SPACING } from '@/components/ScreenNavBar';
import { Box, Row } from '@/components/lib/components';
import {
    CLOSE_ICON_SIZE,
    PROMO_HEADER_HEIGHT,
    getPromoHeaderBarScrollOffset,
} from '@/feature/promohub/utils/promotions-utils';
import { PromotionThemeDocument } from '@/types/prismic.generated';

import { usePromoTheme } from '../hooks/use-promo-theme';
import { GlowShape } from './OvalGlow';

interface PromoHeaderProps {
    theme: PromotionThemeDocument['data'];
    headerHeight: SharedValue<number>;
    scrollY: SharedValue<number>;
    showNavigationButton?: boolean;
    showRafHistory?: boolean;
    infoIconPress?: () => void;
    historyIconPress?: () => void;
}

export const PromoHeaderBackground = ({
    theme,
    headerHeight,
    scrollY,
    showNavigationButton,
    showRafHistory,
    infoIconPress,
    historyIconPress,
}: PromoHeaderProps) => {
    const { width } = useWindowDimensions();
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();

    const { background_color, top_glow_color, navigation_button_color } = usePromoTheme(theme);

    const barScrollOffset = getPromoHeaderBarScrollOffset(insets.top);
    // show the bar right away once the title reaches the header's bottom edge
    const opacityValue = useDerivedValue(() => {
        return scrollY.value >= barScrollOffset ? 1 : 0;
    });

    const navigationBackground = useAnimatedStyle(() => {
        return {
            opacity: opacityValue.value,
        };
    });

    const glowContainerStyle = useAnimatedStyle(() => {
        return {
            width,
            height: headerHeight.value,
        };
    });

    return (
        <Animated.View style={[styles.navigationContainer]}>
            <Animated.View
                style={[{ backgroundColor: background_color }, navigationBackground, styles.navigationBackground]}
            />
            <Animated.View style={[styles.topGlowContainer, navigationBackground]}>
                <Animated.View style={glowContainerStyle}>
                    <GlowShape
                        x={width / 2 - 120}
                        y={-80}
                        width={240}
                        height={240}
                        color={top_glow_color}
                        opacity={1}
                        blur={90}
                    />
                </Animated.View>
            </Animated.View>
            {showNavigationButton ? (
                <Animated.View style={{ paddingTop: insets.top + SHEET_NAV_BAR_TOP_SPACING }}>
                    <View style={[styles.closeIcon]}>
                        <TouchableOpacity hitSlop={20} activeOpacity={0.8} onPress={() => navigation.goBack()}>
                            <CloseIcon
                                color={navigation_button_color}
                                width={CLOSE_ICON_SIZE}
                                height={CLOSE_ICON_SIZE}
                            />
                        </TouchableOpacity>
                    </View>
                </Animated.View>
            ) : null}
            {showRafHistory ? (
                <Box style={{ paddingTop: insets.top }}>
                    <Row alignSelf={'flex-end'}>
                        <TouchableOpacity
                            hitSlop={20}
                            activeOpacity={0.8}
                            style={styles.rafHistoryIcons}
                            onPress={infoIconPress}
                        >
                            <InfoOutlinedIcon />
                        </TouchableOpacity>
                        <TouchableOpacity
                            hitSlop={20}
                            activeOpacity={0.8}
                            style={styles.rafHistoryIcons}
                            onPress={historyIconPress}
                        >
                            <HistoryIcon />
                        </TouchableOpacity>
                    </Row>
                </Box>
            ) : null}
            {!showNavigationButton && !showRafHistory ? (
                <Box style={{ paddingTop: insets.top }} height={PROMO_HEADER_HEIGHT + insets.top} />
            ) : null}
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    navigationContainer: {
        position: 'absolute',
        zIndex: 1,
        width: '100%',
        overflow: 'hidden',
    },
    navigationBackground: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: -1,
    },
    topGlowContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    },
    bottomGlowContainer: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: -1,
    },
    closeIcon: {
        paddingHorizontal: 16,
        width: '100%',
        height: SCREEN_NAV_BAR_HEIGHT,
        justifyContent: 'center',
    },
    rafHistoryIcons: {
        padding: 12,
    },
});
